import { Capacitor } from '@capacitor/core';
import { trackEvent } from './analyticsService';

/**
 * In-App Purchase Service using cordova-plugin-purchase
 * Based on the official CdvPurchase example
 * Supports iOS and Android in-app purchases, falls back to PayPal for web
 */
class InAppPurchaseService {
    constructor() {
        this.isNative = Capacitor.isNativePlatform();
        this.store = null;
        this.CdvPurchase = null;
        this.initialized = false;
        this.initPromise = null;
        this.pendingPurchases = new Map(); // Track pending purchases by productId
        
        if (this.isNative) {
            this.initPromise = this.initializeStore();
        }
    }
    
    /**
     * Wait for deviceready or Capacitor ready
     */
    async waitForDeviceReady() {
        return new Promise((resolve) => {
            // Check if already ready
            if (document.readyState === 'complete' || 
                (typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform())) {
                // For Capacitor, we can proceed immediately
                resolve();
                return;
            }
            
            // Wait for deviceready (Cordova) or DOMContentLoaded
            const checkReady = () => {
                if (document.readyState === 'complete' || 
                    (typeof window !== 'undefined' && window.device)) {
                    resolve();
                } else {
                    setTimeout(checkReady, 100);
                }
            };
            
            // Listen for deviceready (Cordova)
            if (typeof document !== 'undefined') {
                document.addEventListener('deviceready', resolve, { once: true });
            }
            
            // Fallback: wait for DOMContentLoaded
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', resolve, { once: true });
            } else {
                checkReady();
            }
            
            // Timeout after 5 seconds
            setTimeout(() => {
                resolve(); // Resolve anyway to prevent hanging
            }, 5000);
        });
    }
    
    /**
     * Initialize the store plugin
     * Based on the working example from CdvPurchase documentation
     */
    async initializeStore() {
        if (this.initialized) return;
        
        // Wait for device ready
        await this.waitForDeviceReady();
        
        // Wait for CdvPurchase to be available
        return new Promise((resolve) => {
            const checkStore = () => {
                // Check for CdvPurchase (newer API)
                if (typeof window !== 'undefined' && window.CdvPurchase) {
                    this.CdvPurchase = window.CdvPurchase;
                    this.store = window.CdvPurchase.store;
                    
                    if (this.store) {
                        this.setupStore();
                        resolve();
                        return;
                    }
                }
                
                // Fallback to window.store (older versions)
                if (typeof window !== 'undefined' && window.store) {
                    this.store = window.store;
                    this.setupStore();
                    resolve();
                    return;
                }
                
                // Check again after a short delay
                setTimeout(checkStore, 100);
            };
            
            checkStore();
            
            // Timeout after 10 seconds
            setTimeout(() => {
                if (!this.initialized) {
                    resolve(); // Resolve anyway to prevent hanging
                }
            }, 10000);
        });
    }
    
    /**
     * Setup store configuration and event handlers
     */
    setupStore() {
        if (!this.store) return;
        
        const { ProductType, Platform } = this.CdvPurchase || {};
        
        // Get platform constants
        const getPlatform = () => {
            const platform = Capacitor.getPlatform();
            if (Platform) {
                return platform === 'ios' ? Platform.APPLE_APPSTORE : 
                       platform === 'android' ? Platform.GOOGLE_PLAY : null;
            }
            // Fallback for older API
            return platform === 'ios' ? this.store.PLATFORM_APPLE_APPSTORE :
                   platform === 'android' ? this.store.PLATFORM_GOOGLE_PLAY : null;
        };
        
        const storePlatform = getPlatform();
        if (!storePlatform) {
            return;
        }
        
        // Get product type constant
        const getProductType = () => {
            if (ProductType) {
                return ProductType.CONSUMABLE;
            }
            // Fallback
            return this.store.CONSUMABLE || 'consumable';
        };
        
        // Register products BEFORE initialization (as per working example)
        this.store.register([
            {
                id: 'tip_0',
                type: getProductType(),
                platform: storePlatform,
            },
            {
                id: 'tip_1',
                type: getProductType(),
                platform: storePlatform,
            },
            {
                id: 'tip_2',
                type: getProductType(),
                platform: storePlatform,
            },
        ]);
        
        // Set verbosity for debugging
        if (this.store.verbosity !== undefined) {
            this.store.verbosity = this.CdvPurchase?.LogLevel?.DEBUG || 3;
        }
        
        // Setup error handler
        this.store.error((error) => {
            // Extract error message
            const errorMessage = error?.message || error?.code || JSON.stringify(error) || 'Unknown error';
            
            // Handle cancelled purchases
            if (error?.code === 'store.PURCHASE_CANCELLED' || 
                error?.code === 'PURCHASE_CANCELLED' ||
                (error?.message && error.message.toLowerCase().includes('cancelled'))) {
                // Track cancellation
                this.pendingPurchases.forEach(({ reject }, productId) => {
                    trackEvent('iap_purchase_cancelled', {
                        platform: Capacitor.getPlatform(),
                        productId: productId,
                        error_code: error?.code,
                        error_message: errorMessage
                    });
                    reject(new Error('Purchase was cancelled'));
                    this.pendingPurchases.delete(productId);
                });
                return;
            }
            
            // Only reject pending purchases for purchase-related errors
            if (error?.code && (
                error.code.includes('PURCHASE') || 
                error.code.includes('ORDER') ||
                error.code === 'store.PURCHASE_ERROR'
            )) {
                // Track purchase errors
                this.pendingPurchases.forEach(({ reject }, productId) => {
                    trackEvent('iap_purchase_error', {
                        platform: Capacitor.getPlatform(),
                        productId: productId,
                        error_code: error?.code,
                        error_message: errorMessage
                    });
                    reject(new Error(errorMessage));
                    this.pendingPurchases.delete(productId);
                });
            }
        });
        
        // Setup global event handlers (as per working example)
        this.store.when()
            .updated((object) => {
                // Re-render the interface on updates
            })
            .approved((transaction) => {
                // Verify approved transactions
                // Try multiple ways to get productId
                let productId = transaction.productId || transaction.id;
                
                // If productId not found, try to get it from the product
                if (!productId && transaction.product) {
                    productId = transaction.product.id || transaction.product.productId;
                }
                
                // If still not found, try to find it by checking all pending purchases
                if (!productId && this.pendingPurchases.size > 0) {
                    // For single pending purchase, use that productId
                    if (this.pendingPurchases.size === 1) {
                        productId = Array.from(this.pendingPurchases.keys())[0];
                    }
                }
                
                if (!productId) {
                    trackEvent('iap_approved_missing_product_id', {
                        platform: Capacitor.getPlatform(),
                        transaction_keys: Object.keys(transaction || {})
                    });
                    return;
                }
                
                // Get the product to check its type
                const product = this.store.get(productId);
                const isConsumable = product && (
                    product.type === 'consumable' || 
                    (this.CdvPurchase && product.type === this.CdvPurchase.ProductType?.CONSUMABLE)
                );
                
                // Track purchase approval
                trackEvent('iap_purchase_approved', {
                    platform: Capacitor.getPlatform(),
                    productId: productId,
                    isConsumable: isConsumable,
                    transactionId: transaction.transactionIdentifier || transaction.id || transaction.transactionId
                });
                
                // For consumables, finish directly without verification
                // For subscriptions, verify first
                if (isConsumable) {
                    // Finish consumables directly
                    this.finishTransaction(transaction, productId);
                } else if (this.store.verify && typeof this.store.verify === 'function') {
                    // Verify subscriptions and other types
                    this.store.verify(transaction);
                } else {
                    // Fallback: finish directly if verify not available
                    this.finishTransaction(transaction, productId);
                }
            })
            .verified((receipt) => {
                // Finish transactions from verified receipts
                // Get the transaction from receipt
                const transaction = receipt.transaction || receipt;
                
                // Try multiple ways to get productId
                let productId = transaction.productId || transaction.id || receipt.productId;
                
                // If productId not found, try to get it from the product
                if (!productId && transaction.product) {
                    productId = transaction.product.id || transaction.product.productId;
                }
                
                // If still not found, try to find it by checking all pending purchases
                if (!productId && this.pendingPurchases.size > 0) {
                    // For single pending purchase, use that productId
                    if (this.pendingPurchases.size === 1) {
                        productId = Array.from(this.pendingPurchases.keys())[0];
                    }
                }
                
                if (!productId) {
                    trackEvent('iap_verified_missing_product_id', {
                        platform: Capacitor.getPlatform(),
                        receipt_keys: Object.keys(receipt || {})
                    });
                    return;
                }
                
                // Track purchase verification
                const transactionId = transaction.transactionIdentifier || transaction.id || transaction.transactionId || receipt.transactionId;
                trackEvent('iap_purchase_verified', {
                    platform: Capacitor.getPlatform(),
                    productId: productId,
                    transactionId: transactionId
                });
                
                // Finish the transaction
                if (this.store.finish && typeof this.store.finish === 'function') {
                    this.store.finish(receipt);
                } else if (transaction && typeof transaction.finish === 'function') {
                    transaction.finish();
                }
                
                // Resolve pending purchase (only if still pending)
                const pending = this.pendingPurchases.get(productId);
                if (pending) {
                    trackEvent('iap_purchase_completed', {
                        platform: Capacitor.getPlatform(),
                        productId: productId,
                        transactionId: transactionId
                    });
                    pending.resolve({
                        productId: productId,
                        transactionId: transactionId
                    });
                    this.pendingPurchases.delete(productId);
                }
            })
            .finished((transaction) => {
                // Handle finished transactions (some platforms fire this after finish)
                // This is a backup handler in case approved/verified didn't catch it
                let productId = transaction.productId || transaction.id;
                
                if (!productId && transaction.product) {
                    productId = transaction.product.id || transaction.product.productId;
                }
                
                if (!productId && this.pendingPurchases.size > 0) {
                    if (this.pendingPurchases.size === 1) {
                        productId = Array.from(this.pendingPurchases.keys())[0];
                    }
                }
                
                if (productId) {
                    // Only resolve if still pending (prevent double resolution)
                    const pending = this.pendingPurchases.get(productId);
                    if (pending) {
                        pending.resolve({
                            productId: productId,
                            transactionId: transaction.transactionIdentifier || transaction.id || transaction.transactionId
                        });
                        this.pendingPurchases.delete(productId);
                    }
                }
            });
        
        // Initialize store with platforms (as per working example)
        const platforms = [];
        if (Platform) {
            if (Capacitor.getPlatform() === 'ios') {
                platforms.push(Platform.APPLE_APPSTORE);
            } else if (Capacitor.getPlatform() === 'android') {
                platforms.push(Platform.GOOGLE_PLAY);
            }
        } else {
            // Fallback: use platform-specific initialization
            if (Capacitor.getPlatform() === 'ios') {
                platforms.push(this.store.PLATFORM_APPLE_APPSTORE);
            } else if (Capacitor.getPlatform() === 'android') {
                platforms.push(this.store.PLATFORM_GOOGLE_PLAY);
            }
        }
        
        if (platforms.length > 0 && typeof this.store.initialize === 'function') {
            this.store.initialize(platforms);
        } else if (typeof this.store.initialize === 'function') {
            // Fallback: initialize without platform array
            this.store.initialize();
        }
        
        // Wait for store to be ready
        this.store.ready(() => {
            this.initialized = true;
        });
        
        // Update store to load products
        if (typeof this.store.update === 'function') {
            this.store.update();
        } else if (typeof this.store.refresh === 'function') {
            this.store.refresh();
        }
    }
    
    /**
     * Finish a transaction
     */
    finishTransaction(transaction, productId = null) {
        if (!transaction) return;
        
        // Try to get productId from transaction if not provided
        if (!productId) {
            productId = transaction.productId || transaction.id;
            
            // If productId not found, try to get it from the product
            if (!productId && transaction.product) {
                productId = transaction.product.id || transaction.product.productId;
            }
            
            // If still not found, try to find it by checking all pending purchases
            if (!productId && this.pendingPurchases.size > 0) {
                // For single pending purchase, use that productId
                if (this.pendingPurchases.size === 1) {
                    productId = Array.from(this.pendingPurchases.keys())[0];
                }
            }
        }
        
        if (!productId) {
            trackEvent('iap_finish_missing_product_id', {
                platform: Capacitor.getPlatform(),
                transaction_keys: Object.keys(transaction || {})
            });
            return;
        }
        
        // Finish the transaction
        if (typeof transaction.finish === 'function') {
            transaction.finish();
        } else if (this.store && typeof this.store.finish === 'function') {
            this.store.finish(transaction);
        }
        
        // Resolve pending purchase
        const pending = this.pendingPurchases.get(productId);
        if (pending) {
            const transactionId = transaction.transactionIdentifier || transaction.id || transaction.transactionId;
            trackEvent('iap_transaction_finished', {
                platform: Capacitor.getPlatform(),
                productId: productId,
                transactionId: transactionId
            });
            pending.resolve({
                productId: productId,
                transactionId: transactionId
            });
            this.pendingPurchases.delete(productId);
        } else {
            trackEvent('iap_finish_no_pending_purchase', {
                platform: Capacitor.getPlatform(),
                productId: productId,
                pending_purchases: Array.from(this.pendingPurchases.keys())
            });
        }
    }
    
    /**
     * Ensure store is initialized before use
     */
    async ensureInitialized() {
        if (!this.isNative) {
            throw new Error('In-app purchases are only available on native platforms');
        }
        
        if (!this.initialized && this.initPromise) {
            await this.initPromise;
        }
        
        if (!this.store) {
            throw new Error('Store plugin not available');
        }
        
        // Wait for store to be ready
        if (!this.initialized) {
            await new Promise((resolve) => {
                if (this.initialized) {
                    resolve();
                    return;
                }
                this.store.ready(() => {
                    this.initialized = true;
                    resolve();
                });
                // Timeout
                setTimeout(() => {
                    if (!this.initialized) {
                        resolve(); // Resolve anyway
                    }
                }, 5000);
            });
        }
    }
    
    /**
     * Load products from App Store/Play Store
     * @param {string[]} productIds - Array of product IDs
     */
    async loadProducts(productIds) {
        await this.ensureInitialized();
        
        return new Promise((resolve, reject) => {
            let resolved = false;
            let checkCount = 0;
            const maxChecks = 50; // Check up to 50 times (5 seconds total with 100ms intervals)
            
            const checkProducts = () => {
                if (resolved) return;
                
                const products = [];
                const invalidIds = [];
                let allLoaded = true;
                
                productIds.forEach(productId => {
                    const product = this.store.get(productId);
                    
                    if (product && this.isProductValid(product)) {
                        // Extract price from various possible locations
                        // Priority: raw.price > offers[0].pricingPhases[0].price > product.price
                        let price = product.price;
                        let currency = product.currency;
                        let localizedPrice = product.priceAsString || product.localizedPrice;
                        
                        // Try product.raw.price first (from the log, this is where the formatted price is)
                        if (product.raw && product.raw.price) {
                            localizedPrice = product.raw.price; // Formatted price string like "$9.99"
                            currency = product.raw.currency || currency;
                            // Try to extract numeric price if priceMicros is available
                            if (product.raw.priceMicros) {
                                price = product.raw.priceMicros / 1000000; // Convert micros to dollars
                            } else if (!price) {
                                price = product.raw.price; // Fallback to string price
                            }
                        }
                        
                        // Try offers[0].pricingPhases[0].price as fallback
                        if (!localizedPrice && product.offers && product.offers.length > 0) {
                            const offer = product.offers[0];
                            if (offer.pricingPhases && offer.pricingPhases.length > 0) {
                                const pricingPhase = offer.pricingPhases[0];
                                localizedPrice = pricingPhase.price; // Formatted price string
                                currency = pricingPhase.currency || currency;
                                if (pricingPhase.priceMicros && !price) {
                                    price = pricingPhase.priceMicros / 1000000; // Convert micros to dollars
                                } else if (!price) {
                                    price = pricingPhase.price; // Fallback to string price
                                }
                            }
                        }
                        
                        const productData = {
                            productId: product.id,
                            price: price,
                            currency: currency,
                            localizedPrice: localizedPrice,
                            title: product.title,
                            description: product.description
                        };
                        products.push(productData);
                    } else {
                        invalidIds.push(productId);
                        allLoaded = false;
                    }
                });
                
                // If all products are loaded or we've checked enough times, resolve
                if (allLoaded || checkCount >= maxChecks) {
                    resolved = true;
                    resolve({
                        products,
                        invalidProductIds: invalidIds
                    });
                } else {
                    // Check again after a short delay
                    checkCount++;
                    setTimeout(checkProducts, 100);
                }
            };
            
            // Start checking for products
            checkProducts();
            
            // Also trigger update to ensure products are loaded
            if (typeof this.store.update === 'function') {
                this.store.update();
            } else if (typeof this.store.refresh === 'function') {
                this.store.refresh();
            }
            
            // Timeout after 10 seconds
            setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    // Final check before resolving
                    const products = [];
                    const invalidIds = [];
                    
                    productIds.forEach(productId => {
                        const product = this.store.get(productId);
                        
                        if (product && this.isProductValid(product)) {
                            // Extract price from various possible locations
                            // Priority: raw.price > offers[0].pricingPhases[0].price > product.price
                            let price = product.price;
                            let currency = product.currency;
                            let localizedPrice = product.priceAsString || product.localizedPrice;
                            
                            // Try product.raw.price first (from the log, this is where the formatted price is)
                            if (product.raw && product.raw.price) {
                                localizedPrice = product.raw.price; // Formatted price string like "$9.99"
                                currency = product.raw.currency || currency;
                                // Try to extract numeric price if priceMicros is available
                                if (product.raw.priceMicros) {
                                    price = product.raw.priceMicros / 1000000; // Convert micros to dollars
                                } else if (!price) {
                                    price = product.raw.price; // Fallback to string price
                                }
                            }
                            
                            // Try offers[0].pricingPhases[0].price as fallback
                            if (!localizedPrice && product.offers && product.offers.length > 0) {
                                const offer = product.offers[0];
                                if (offer.pricingPhases && offer.pricingPhases.length > 0) {
                                    const pricingPhase = offer.pricingPhases[0];
                                    localizedPrice = pricingPhase.price; // Formatted price string
                                    currency = pricingPhase.currency || currency;
                                    if (pricingPhase.priceMicros && !price) {
                                        price = pricingPhase.priceMicros / 1000000; // Convert micros to dollars
                                    } else if (!price) {
                                        price = pricingPhase.price; // Fallback to string price
                                    }
                                }
                            }
                            
                            const productData = {
                                productId: product.id,
                                price: price,
                                currency: currency,
                                localizedPrice: localizedPrice,
                                title: product.title,
                                description: product.description
                            };
                            products.push(productData);
                        } else {
                            invalidIds.push(productId);
                        }
                    });
                    
                    resolve({
                        products,
                        invalidProductIds: invalidIds
                    });
                }
            }, 10000);
        });
    }
    
    /**
     * Purchase a product
     * @param {string} productId - Product ID to purchase
     */
    async purchase(productId) {
        await this.ensureInitialized();
        
        // First, ensure the product is loaded and valid
        await this.waitForProduct(productId);
        
        return new Promise((resolve, reject) => {
            // Get platform for product lookup (as per working example)
            const { Platform } = this.CdvPurchase || {};
            const platform = Capacitor.getPlatform();
            let storePlatform = null;
            
            if (Platform) {
                storePlatform = platform === 'ios' ? Platform.APPLE_APPSTORE : 
                               platform === 'android' ? Platform.GOOGLE_PLAY : null;
            } else {
                storePlatform = platform === 'ios' ? this.store.PLATFORM_APPLE_APPSTORE :
                               platform === 'android' ? this.store.PLATFORM_GOOGLE_PLAY : null;
            }
            
            // Get product with platform (as per working example)
            const product = storePlatform ? this.store.get(productId, storePlatform) : this.store.get(productId);
            
            if (!product) {
                reject(new Error(`Product ${productId} not found. Make sure to load products first.`));
                return;
            }
            
            if (!this.isProductValid(product)) {
                reject(new Error(`Product ${productId} is not valid. Make sure the product has offers loaded.`));
                return;
            }
            
            // Store pending purchase
            this.pendingPurchases.set(productId, { resolve, reject });
            
            // Track purchase initiation
            trackEvent('iap_purchase_started', {
                platform: Capacitor.getPlatform(),
                productId: productId,
                price: product.price,
                currency: product.currency
            });
            
            // Use store.order() with offer (as per working example)
            // Get the offer using getOffer() method or fallback to offers[0]
            let offer = null;
            
            if (product.offers && product.offers.length > 0) {
                // Try to get offer using getOffer() method (preferred)
                const offerId = product.offers[0].id || '$'; // Default offer ID is usually '$'
                if (typeof product.getOffer === 'function') {
                    offer = product.getOffer(offerId);
                } else {
                    // Fallback: use first offer directly
                    offer = product.offers[0];
                }
            }
            
            if (offer && this.store.order && typeof this.store.order === 'function') {
                // Use store.order(offer) as per working example
                this.store.order(offer);
            } else if (product.order && typeof product.order === 'function') {
                // Fallback: use product.order()
                product.order();
            } else {
                trackEvent('iap_purchase_init_failed', {
                    platform: Capacitor.getPlatform(),
                    productId: productId,
                    reason: 'order_method_not_available'
                });
                reject(new Error('Unable to initiate purchase - order method not available'));
                this.pendingPurchases.delete(productId);
                return;
            }
            
            // Timeout after 60 seconds
            setTimeout(() => {
                if (this.pendingPurchases.has(productId)) {
                    trackEvent('iap_purchase_timeout', {
                        platform: Capacitor.getPlatform(),
                        productId: productId,
                        timeout_seconds: 60
                    });
                    this.pendingPurchases.delete(productId);
                    reject(new Error('Purchase timeout'));
                }
            }, 60000);
        });
    }
    
    /**
     * Check if a product is valid (has offers and is loaded)
     */
    isProductValid(product) {
        if (!product) return false;
        // Product is valid if it has offers (as per working example)
        return product.offers && product.offers.length > 0;
    }
    
    /**
     * Wait for a product to be loaded and valid
     * @param {string} productId - Product ID to wait for
     */
    async waitForProduct(productId) {
        return new Promise((resolve, reject) => {
            // Get platform for product lookup
            const { Platform } = this.CdvPurchase || {};
            const platform = Capacitor.getPlatform();
            let storePlatform = null;
            
            if (Platform) {
                storePlatform = platform === 'ios' ? Platform.APPLE_APPSTORE : 
                               platform === 'android' ? Platform.GOOGLE_PLAY : null;
            } else {
                storePlatform = platform === 'ios' ? this.store.PLATFORM_APPLE_APPSTORE :
                               platform === 'android' ? this.store.PLATFORM_GOOGLE_PLAY : null;
            }
            
            // Check if product is already valid
            const product = storePlatform ? this.store.get(productId, storePlatform) : this.store.get(productId);
            if (product && this.isProductValid(product)) {
                resolve();
                return;
            }
            
            let resolved = false;
            let timeoutId = null;
            
            // Set up a listener for product updates
            const checkProduct = () => {
                if (resolved) return;
                
                const product = storePlatform ? this.store.get(productId, storePlatform) : this.store.get(productId);
                if (product && this.isProductValid(product)) {
                    resolved = true;
                    if (timeoutId) clearTimeout(timeoutId);
                    resolve();
                }
            };
            
            // Listen for product updates using the store.when API
            try {
                const productRef = this.store.when(productId);
                if (productRef && typeof productRef.updated === 'function') {
                    productRef.updated(checkProduct);
                }
            } catch (e) {
                // Silently fail - product will be checked on timeout
            }
            
            // Also listen for store ready events
            this.store.ready(() => {
                checkProduct();
            });
            
            // Don't trigger update here - it's already being called and throttled
            // The product should be loaded from the initial update
            
            // Timeout after 10 seconds
            timeoutId = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    const product = storePlatform ? this.store.get(productId, storePlatform) : this.store.get(productId);
                    if (product && this.isProductValid(product)) {
                        resolve();
                    } else {
                        reject(new Error(`Product ${productId} failed to load. Please try again.`));
                    }
                }
            }, 10000);
        });
    }
    
    /**
     * Restore previous purchases
     * Based on cordova-plugin-purchase v13 API
     * @returns {Promise<{restoredProducts: string[]}>} Array of restored product IDs
     */
    async restorePurchases() {
        await this.ensureInitialized();
        
        // Check if restorePurchases returns a Promise (v13 API)
        if (typeof this.store.restorePurchases === 'function') {
            try {
                // v13 API: restorePurchases() returns a Promise
                const result = await this.store.restorePurchases();
                
                // Collect owned products
                const restoredProducts = [];
                const { Platform } = this.CdvPurchase || {};
                const platform = Capacitor.getPlatform();
                let storePlatform = null;
                
                if (Platform) {
                    storePlatform = platform === 'ios' ? Platform.APPLE_APPSTORE : 
                                   platform === 'android' ? Platform.GOOGLE_PLAY : null;
                } else {
                    storePlatform = platform === 'ios' ? this.store.PLATFORM_APPLE_APPSTORE :
                                   platform === 'android' ? this.store.PLATFORM_GOOGLE_PLAY : null;
                }
                
                // Check all registered products for ownership
                const registeredProducts = ['tip_0', 'tip_1', 'tip_2'];
                registeredProducts.forEach(productId => {
                    const product = storePlatform ? this.store.get(productId, storePlatform) : this.store.get(productId);
                    if (product && product.owned) {
                        restoredProducts.push(productId);
                    }
                });
                
                return { restoredProducts };
            } catch (error) {
                // If restore fails, return empty array (user might not have any purchases)
                trackEvent('iap_restore_failed', {
                    platform: Capacitor.getPlatform(),
                    error_message: error?.message || 'Unknown error',
                    error_name: error?.name
                });
                return { restoredProducts: [] };
            }
        }
        
        // Fallback: Event-based API (older versions)
        return new Promise((resolve, reject) => {
            const restoredProducts = [];
            let resolved = false;
            
            // Listen for restored purchases
            const updateHandler = (product) => {
                if (product && product.owned && !restoredProducts.includes(product.id)) {
                    restoredProducts.push(product.id);
                }
            };
            
            // Listen for product updates
            this.store.when('product').updated(updateHandler);
            
            // Handle restore completion
            this.store.when('product').restored(() => {
                if (!resolved) {
                    resolved = true;
                    resolve({ restoredProducts });
                }
            });
            
            // Handle restore errors
            const errorHandler = (error) => {
                if (!resolved && (error.code === 'store.PURCHASE_ERROR' || error.code === 'store.RESTORE_ERROR')) {
                    // This might be expected if there are no purchases to restore
                    if (!resolved) {
                        resolved = true;
                        resolve({ restoredProducts });
                    }
                }
            };
            
            // Temporarily add error handler
            this.store.error(errorHandler);
            
            // Restore purchases using fallback method
            if (typeof this.store.restore === 'function') {
                this.store.restore();
            } else {
                // Remove error handler before rejecting
                resolved = true;
                reject(new Error('Restore purchases not available'));
                return;
            }
            
            // Timeout after 10 seconds
            setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    resolve({ restoredProducts });
                }
            }, 10000);
        });
    }
    
    /**
     * Check if a product is owned
     * @param {string} productId - Product ID to check
     * @returns {boolean} True if product is owned
     */
    async isProductOwned(productId) {
        if (!this.isNative || !this.store) {
            return false;
        }
        
        await this.ensureInitialized();
        
        const { Platform } = this.CdvPurchase || {};
        const platform = Capacitor.getPlatform();
        let storePlatform = null;
        
        if (Platform) {
            storePlatform = platform === 'ios' ? Platform.APPLE_APPSTORE : 
                           platform === 'android' ? Platform.GOOGLE_PLAY : null;
        } else {
            storePlatform = platform === 'ios' ? this.store.PLATFORM_APPLE_APPSTORE :
                           platform === 'android' ? this.store.PLATFORM_GOOGLE_PLAY : null;
        }
        
        const product = storePlatform ? this.store.get(productId, storePlatform) : this.store.get(productId);
        return product && product.owned === true;
    }
    
    /**
     * Check if in-app purchases are available
     */
    isAvailable() {
        return this.isNative && this.store !== null;
    }
}

export const inAppPurchaseService = new InAppPurchaseService();
